window.deferredPWAEvent = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  window.deferredPWAEvent = e;
  window.dispatchEvent(new CustomEvent('pwa:deferredpromptready'));
});

class PWAInstaller {
  constructor() {
    this.deferredPrompt = null;
    this.installButton = null;
    this.isInstalled = false;
    this.debugMode = true;
    this.init();
  }

  log(message, ...args) {
    if (this.debugMode) {
    }
  }

  error(message, ...args) {
  }

  init() {
    this.log('Initializing PWA Installer...');
    
    if (localStorage.getItem('pwa_installed_flag') === 'true') {
      this.isInstalled = true;
      this.log('App is already installed (flag set)');
      return;
    }
    
    if (!('serviceWorker' in navigator)) {
      this.error('Service Worker not supported in this browser');
      return;
    }
    
    this.log('Service Worker API available');
    
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (isStandalone) {
      this.isInstalled = true;
      this.log('App is launched in standalone mode');
      
      if (window.gtag) {
        window.gtag('event', 'pwa_app_launch', {
          event_category: 'PWA',
          event_label: 'Launched from Homescreen'
        });
      }
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'pwa_app_launch',
        'platform': window.navigator.standalone ? 'ios_safari' : 'android_chrome'
      });
      return;
    }

    setTimeout(() => {
      this.showInstallPromo();
    }, 1000);
    
    this.log('App not installed yet, checking for early prompt or waiting...');

    if (window.deferredPWAEvent) {
      this.log('beforeinstallprompt event already captured globally');
      this.deferredPrompt = window.deferredPWAEvent;
      try {
        window.dispatchEvent(new CustomEvent('pwa:deferredpromptready'));
      } catch (e) {
        this.error('Failed to dispatch deferred prompt ready event', e);
      }
    }

    window.addEventListener('beforeinstallprompt', (e) => {
      this.log('beforeinstallprompt event fired! Install criteria met!');
      localStorage.removeItem('pwa_installed_flag');
      e.preventDefault();
      this.deferredPrompt = e;
      window.deferredPWAEvent = e;
      this.log('Install prompt stored, showing UI...');
      
      try {
        window.dispatchEvent(new CustomEvent('pwa:deferredpromptready'));
      } catch (eventError) {
        this.error('Failed to dispatch deferred prompt ready event', eventError);
      }
      
      this.showInstallPromo();
    });

    window.addEventListener('appinstalled', () => {
      this.log('App installed successfully!');
      localStorage.setItem('pwa_installed_flag', 'true');
      this.isInstalled = true;
      this.hideInstallPromo();
      this.deferredPrompt = null;
      
      if (window.gtag) {
        window.gtag('event', 'pwa_installed', {
          event_category: 'PWA',
          event_label: 'Install Successful'
        });
      }
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'pwa_install_success',
        'platform': 'android_chrome'
      });
    });

    this.checkIOSInstallPrompt();
    
    setTimeout(() => this.checkPWARequirements(), 2000);
  }

  async checkPWARequirements() {
    this.log('Checking PWA requirements...');
    
    const isHttps = location.protocol === 'https:' || location.hostname === 'localhost';
    this.log(`HTTPS: ${isHttps ? 'Yes' : 'No'} (${location.protocol})`);
    
    const manifestLink = document.querySelector('link[rel="manifest"]');
    this.log(`Manifest link: ${manifestLink ? 'Yes' : 'No'}`, manifestLink?.href);
    
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        this.log(`Service Worker registrations: ${registrations.length > 0 ? 'Yes' : 'No'}`, registrations);
        
        if (registrations.length > 0) {
          const sw = registrations[0];
          this.log(`Service Worker state: ${sw.active ? 'active' : sw.installing ? 'installing' : 'waiting'}`);
          
          if (sw.installing || sw.waiting) {
            this.log('Waiting for service worker to activate...');
            await navigator.serviceWorker.ready;
            this.log('Service Worker is now active!');
          }
        } else {
          this.error('No service worker registered! This is the main issue.');
          this.log('Checking if service worker script is present...');
          
          setTimeout(() => this.recheckServiceWorker(), 3000);
        }
      } catch (error) {
        this.error('Error checking service worker:', error);
      }
    }
    
    if (!this.deferredPrompt) {
      this.log('Waiting for beforeinstallprompt event...');
      this.log('Requirements checklist:');
      this.log(`  1. HTTPS: ${isHttps ? 'Yes' : 'No'}`);
      this.log(`  2. Manifest: ${manifestLink ? 'Yes' : 'No'}`);
      this.log('  3. Service Worker: Check above');
      this.log('  4. Not already installed: Pending');
      this.log('  5. Chrome/Edge browser: Pending');
    }
  }

  async recheckServiceWorker() {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        this.log('Service Worker now registered! Waiting for beforeinstallprompt...');
        await navigator.serviceWorker.ready;
        this.log('Service Worker is active and ready!');
      } else {
        this.error('Service Worker still not registered after 3 seconds');
      }
    }
  }

  checkIOSInstallPrompt() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);

    if (isIOS && !isInStandaloneMode) {
      this.showIOSInstallInstructions();
    }
  }

  showIOSInstallInstructions() {
    const iosBanner = document.createElement('div');
    iosBanner.id = 'ios-install-banner';
    iosBanner.className = 'pwa-ios-banner';
    iosBanner.innerHTML = ``;

    if (localStorage.getItem('pwa_ios_banner_dismissed') === 'true') {
      return;
    }

    document.body.appendChild(iosBanner);

    const closeBtn = iosBanner.querySelector('.pwa-ios-banner__close');
    closeBtn.addEventListener('click', () => {
      iosBanner.remove();
      localStorage.setItem('pwa_ios_banner_dismissed', 'true');
    });
  }

  showInstallPromo() {
    return;
  }

  isMobileDevice() {
    const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const coarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const narrowViewport = window.matchMedia && window.matchMedia('(max-width: 767px)').matches;
    return uaMobile || (coarsePointer && narrowViewport);
  }

  createInstallUI() {
    if (document.getElementById('pwa-install-container')) {
      return;
    }

    const logoUrl = this.getPWALogoUrl();

    const installContainer = document.createElement('div');
    installContainer.id = 'pwa-install-container';
    installContainer.className = 'pwa-install-banner';
    installContainer.innerHTML = `
      <div class="pwa-install-banner__inner">
      
        <div class="pwa-install-banner__content">
        <button type="button" id="pwa-dismiss-btn" class="pwa-dismiss-btn" aria-label="Dismiss">&times;</button>
          <div class="pwa-install-banner__icon">
            <img src="${logoUrl}" alt="Ackermans logo">
          </div>
          <div class="pwa-install-banner__text">
            <span>Add the app to your home screen for faster shopping.</span>
          </div>
          <div class="pwa-install-banner__actions">
          <button type="button" id="pwa-install-btn" class="pwa-install-btn">Install</button>
          
        </div>
        </div>
        
      </div>
    `;

    document.body.appendChild(installContainer);

    this.installButton = document.getElementById('pwa-install-btn');
    const dismissBtn = document.getElementById('pwa-dismiss-btn');

    if (this.installButton) {
      this.installButton.addEventListener('click', () => this.promptInstall());
    }
    if (dismissBtn) {
      dismissBtn.addEventListener('click', () => this.dismissInstallPromo());
    }

    setTimeout(() => {
      installContainer.classList.add('pwa-install-banner--visible');
    }, 500);
  }

  async promptInstall() {
    if (!this.deferredPrompt) {
      return;
    }

    try {
      this.deferredPrompt.prompt();

      const { outcome } = await this.deferredPrompt.userChoice;

      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome
        });
      }

      if (outcome === 'accepted') {
        this.hideInstallPromo();
      }

      this.deferredPrompt = null;
    } catch (error) {
    }
  }

  showInstallModal() {
    let iconUrl = this.getAppIcon();
    
    const brandingImg = document.querySelector('.pwa-install-prompt__branding img');
    if (brandingImg && brandingImg.src) {
      iconUrl = brandingImg.src;
    } else {
      const manifestLink = document.querySelector('link[rel="manifest"]');
      if (manifestLink) {
        const manifestPath = manifestLink.href;
        const manifestBase = manifestPath.substring(0, manifestPath.lastIndexOf('/'));
        iconUrl = manifestBase + '/logo-pwa.svg';
      }
    }
    
    const modal = document.createElement('div');
    modal.id = 'pwa-install-modal';
    modal.className = 'pwa-modal';
    modal.innerHTML = `
      <div class="pwa-modal__overlay"></div>
      <div class="pwa-modal__content">
        <div class="pwa-modal__header">
          <h2>Install app</h2>
        </div>
        <div class="pwa-modal__body">
          <div class="pwa-modal__app-info">
            <div class="pwa-modal__app-icon-wrapper">
              <img src="${iconUrl}" alt="App Icon" class="pwa-modal__app-icon" onerror="this.src='${this.getAppIcon()}'">
            </div>
            <div class="pwa-modal__app-details">
              <h3>${this.getAppName()}</h3>
              <p>${window.location.hostname}</p>
            </div>
          </div>
        </div>
        <div class="pwa-modal__footer">
          <button class="pwa-modal__button pwa-modal__button--secondary" id="pwa-modal-cancel">
            Cancel
          </button>
          <button class="pwa-modal__button pwa-modal__button--primary" id="pwa-modal-install">
            Install
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    setTimeout(() => {
      modal.classList.add('pwa-modal--visible');
    }, 10);

    const cancelBtn = document.getElementById('pwa-modal-cancel');
    const installBtn = document.getElementById('pwa-modal-install');
    const overlay = modal.querySelector('.pwa-modal__overlay');

    const closeModal = () => {
      modal.classList.remove('pwa-modal--visible');
      setTimeout(() => modal.remove(), 300);
    };

    cancelBtn.addEventListener('click', closeModal);
    overlay.addEventListener('click', closeModal);

    installBtn.addEventListener('click', async () => {
      this.deferredPrompt.prompt();

      const { outcome } = await this.deferredPrompt.userChoice;

      if (window.gtag) {
        window.gtag('event', 'pwa_install_prompt', {
          event_category: 'PWA',
          event_label: outcome
        });
      }

      if (outcome === 'accepted') {
        this.hideInstallPromo();
      }

      this.deferredPrompt = null;
      closeModal();
    });
  }

  dismissInstallPromo() {
    const banner = document.getElementById('pwa-install-container');
    if (banner) {
      banner.classList.remove('pwa-install-banner--visible');
      setTimeout(() => banner.remove(), 300);
    }
    
    localStorage.setItem('pwa_install_dismissed', Date.now().toString());
    
    if (window.gtag) {
      window.gtag('event', 'pwa_install_dismissed', {
        event_category: 'PWA',
        event_label: 'Banner Dismissed'
      });
    }
  }

  hideInstallPromo() {
    const banner = document.getElementById('pwa-install-container');
    if (banner) {
      banner.classList.remove('pwa-install-banner--visible');
      setTimeout(() => banner.remove(), 300);
    }
  }

  getAppName() {
    return document.querySelector('meta[property="og:site_name"]')?.content || 
           document.title || 
           'Shop';
  }

  getAppIcon() {
    return document.querySelector('link[rel="icon"]')?.href ||
           document.querySelector('link[rel="shortcut icon"]')?.href ||
           '/favicon.ico';
  }

  getPWALogoUrl() {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      const manifestPath = manifestLink.href;
      const manifestBase = manifestPath.substring(0, manifestPath.lastIndexOf('/'));
      return manifestBase + '/logo-pwa.svg';
    }
    return this.getAppIcon();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.pwaInstaller = new PWAInstaller();
  });
} else {
  window.pwaInstaller = new PWAInstaller();
}
